/**
 * ActionsBucket — Shots/Injections · Referrals · Tasks · Forms
 */
import React from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Zap, Plus, X, Check, Loader2, Send, AlertCircle,
  Syringe, Navigation, ListChecks, Activity, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { BucketHeader, EmptyBucket } from './shared';

/* ═══════════════════════════════════════
   ACTIONS BUCKET (renamed from Tasks)
   Shots/Injections · Referrals · Tasks · Forms
   + Add Action modal
   ═══════════════════════════════════════ */

const ACTION_TYPE_CONFIG = {
  injection: { label: 'Shot/Injection', icon: Syringe, color: 'bg-rose-50 text-rose-700 border-rose-200' },
  referral:  { label: 'Referral',       icon: Navigation, color: 'bg-violet-50 text-violet-700 border-violet-200' },
  task:      { label: 'Task',           icon: ListChecks, color: 'bg-sky-50 text-sky-700 border-sky-200' },
  follow_up: { label: 'Follow-Up',      icon: Activity,   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  screening: { label: 'Screening',      icon: Activity,   color: 'bg-teal-50 text-teal-700 border-teal-200' },
  form:      { label: 'Form',           icon: ListChecks, color: 'bg-gray-50 text-gray-700 border-gray-200' },
};

export function ActionsBucket({ enc, encounterId, fetchEncounter, outputs }) {
  const allActions = (enc?.suggested_actions || []).filter(a =>
    ['task','injection','referral','follow_up','form','screening'].includes(a.action_type)
  );

  const [showAddModal, setShowAddModal] = React.useState(false);
  const [addForm, setAddForm] = React.useState({ action_name:'', action_type:'task', specialty:'', specialist_name:'', summary:'', linked_diagnosis:'', assignee:'nurse', j_code:'' });
  const [adding, setAdding] = React.useState(false);
  const [jcodeSuggestions, setJcodeSuggestions] = React.useState([]);
  const [jcodeLoaded, setJcodeLoaded] = React.useState(false);
  const [staffList, setStaffList] = React.useState([]);
  // Per-action recipient state: { [action_id]: { id, name, role } }
  const [actionRecipients, setActionRecipients] = React.useState({});
  const [recipientPickerOpen, setRecipientPickerOpen] = React.useState(null);

  // Load J-code suggestions
  React.useEffect(() => {
    if (jcodeLoaded) return;
    api.get(`/encounters/${encounterId}/suggest-jcodes`).then(r => {
      setJcodeSuggestions(r.data.jcodes || []);
      setJcodeLoaded(true);
    }).catch(() => { setJcodeLoaded(true); });
  }, [encounterId, jcodeLoaded]);

  // Load staff list for recipient picker (provider-mapped nurses first)
  React.useEffect(() => {
    api.get('/conversations/staff/recipients').then(r => {
      const staff = r.data.staff || [];
      setStaffList(staff);
      // Pre-select recipients: use verbalized assignee_role if available, else mapped nurse
      const primaryMapped = staff.find(s => s.is_primary_mapped);
      const defaults = {};
      allActions.filter(a => a.status === 'pending').forEach(a => {
        const role = a.assignee_role || a.assignee;
        if (role) {
          // Find staff matching the verbalized role
          const match = staff.find(s => s.role === role || s.role?.includes(role));
          if (match) { defaults[a.id] = { id: match.id, name: match.name, role: match.role }; return; }
        }
        // Fall back to primary mapped staff
        if (primaryMapped) defaults[a.id] = { id: primaryMapped.id, name: primaryMapped.name, role: primaryMapped.role };
      });
      setActionRecipients(prev => ({ ...defaults, ...prev }));
    }).catch(() => {});
  }, [encounterId]); // eslint-disable-line

  const dxCodes = (outputs?.billing_suggestion?.diagnosis_codes || [])
    .concat((enc?.note?.problem_oriented_ap || []).map(p => p.icd_code).filter(Boolean));
  const uniqueDxCodes = [...new Set(dxCodes)];

  const handleExecuteAction = async (actionId, action) => {
    const recipient = actionRecipients[actionId];
    try {
      await api.post(`/encounters/${encounterId}/suggested-actions/${actionId}/execute`, {
        action,
        target_user_id: recipient?.id || null,
        target_user_name: recipient?.name || '',
        target_user_role: recipient?.role || 'nurse',
      });
      fetchEncounter();
      if (action === 'send_to_nurse') {
        toast.success(recipient ? `Sent to ${recipient.name}` : 'Sent to nurse');
      } else if (action === 'done_by_provider') {
        toast.success('Done');
      } else {
        toast.success('Dismissed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to execute action');
    }
  };

  const handleAddAction = async () => {
    if (!addForm.action_name.trim()) { toast.error('Action name required'); return; }
    setAdding(true);
    try {
      const payload = {
        ...addForm,
        task_type: addForm.action_type === 'injection' ? 'INJ' : addForm.action_type === 'referral' ? 'REFERRAL' : 'TASK',
      };
      await api.post(`/encounters/${encounterId}/add-action`, payload);
      fetchEncounter();
      setShowAddModal(false);
      setAddForm({ action_name:'', action_type:'task', specialty:'', specialist_name:'', summary:'', linked_diagnosis:'', assignee:'nurse', j_code:'' });
      toast.success('Action added');
    } catch { toast.error('Failed to add action'); }
    finally { setAdding(false); }
  };

  return (
    <div className="space-y-4" data-testid="finalize-actions-bucket">
      <div className="flex items-center justify-between">
        <BucketHeader icon={Zap} title="Actions" count={allActions.length}
          status={allActions.length > 0 && allActions.every(a => a.status !== 'pending') ? 'complete' : allActions.length > 0 ? 'pending' : undefined} />
        <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
          onClick={() => setShowAddModal(true)} data-testid="add-action-btn">
          <Plus className="w-3 h-3" /> Add Action
        </Button>
      </div>

      {/* J-code suggestions banner */}
      {jcodeSuggestions.length > 0 && (
        <div className="rounded-lg border border-rose-100 bg-rose-50/30 p-3" data-testid="jcode-suggestions">
          <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wider mb-2">
            Injections detected in note — J-codes suggested
          </p>
          <div className="space-y-1">
            {jcodeSuggestions.map(j => (
              <div key={j.code} className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="text-[9px] font-mono bg-rose-50 text-rose-700 border-rose-200 shrink-0">{j.code}</Badge>
                <span className="flex-1 text-muted-foreground">{j.description}</span>
                <button onClick={() => {
                  setAddForm(f => ({ ...f, action_type: 'injection', action_name: j.description, j_code: j.code }));
                  setShowAddModal(true);
                }} className="text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors">
                  Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Cards */}
      {allActions.length === 0 && jcodeSuggestions.length === 0 && (
        <div className="text-center py-8">
          <div className="p-3 rounded-2xl bg-muted/50 mx-auto w-fit mb-3">
            <Zap className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">No actions captured — click "+ Add Action" to add shots, referrals, or tasks</p>
        </div>
      )}

      <div className="space-y-2">
        {allActions.map((action, i) => {
          const isPending = action.status === 'pending';
          const cfg = ACTION_TYPE_CONFIG[action.action_type] || ACTION_TYPE_CONFIG.task;
          const CfgIcon = cfg.icon;
          return (
            <div key={action.id}
              className={`rounded-lg border p-3 ${!isPending ? 'opacity-60' : ''}`}
              data-testid={`finalize-action-${i}`}>
              <div className="flex items-start gap-2">
                <div className={`p-1.5 rounded-md border shrink-0 mt-0.5 ${cfg.color}`}>
                  <CfgIcon className="w-3 h-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{action.action_name}</span>
                    <Badge variant="outline" className={`text-[8px] px-1.5 ${cfg.color}`}>{cfg.label}</Badge>
                    {action.j_code && (
                      <Badge variant="outline" className="text-[8px] font-mono bg-rose-50 text-rose-700">{action.j_code}</Badge>
                    )}
                    {action.source === 'ai_extracted' && (
                      <Badge variant="outline" className="text-[8px] bg-violet-50 text-violet-600">AI detected</Badge>
                    )}
                  </div>
                  {(action.specialist_name || action.specialty) && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {action.specialist_name}{action.specialty ? ` — ${action.specialty}` : ''}
                    </p>
                  )}
                  {action.summary && <p className="text-xs text-muted-foreground mt-0.5">{action.summary}</p>}
                  {action.linked_diagnosis && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                      Linked: <span className="font-mono">{action.linked_diagnosis}</span>
                    </p>
                  )}
                </div>
              </div>

              {isPending ? (
                <div className="flex gap-1.5 mt-2 ml-8 flex-wrap items-center">
                  {/* Recipient picker + Send button */}
                  <div className="relative flex items-center gap-0">
                    {/* Recipient display/dropdown */}
                    <button
                      onClick={() => setRecipientPickerOpen(recipientPickerOpen === action.id ? null : action.id)}
                      className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground border border-r-0 rounded-l-md px-2 py-1.5 bg-background hover:bg-accent transition-colors"
                      data-testid={`recipient-btn-${i}`}>
                      <span>{actionRecipients[action.id]?.name || 'Select...'}</span>
                      <ChevronDown className="w-2.5 h-2.5" />
                    </button>
                    <Button size="sm" className="h-7 text-[10px] px-3 gap-1 rounded-l-none"
                      onClick={() => handleExecuteAction(action.id, 'send_to_nurse')}
                      data-testid={`action-send-nurse-${i}`}>
                      <Send className="w-3 h-3" /> Send
                    </Button>
                    {/* Recipient dropdown */}
                    {recipientPickerOpen === action.id && (
                      <div className="absolute left-0 top-full mt-1 w-52 bg-card rounded-xl border shadow-xl z-30 py-1 animate-in fade-in-0 slide-in-from-top-1 duration-100"
                        data-testid={`recipient-picker-${i}`}>
                        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1.5">Send to</p>
                        {staffList.map(s => (
                          <button key={s.id}
                            onClick={() => {
                              setActionRecipients(prev => ({ ...prev, [action.id]: { id: s.id, name: s.name, role: s.role } }));
                              setRecipientPickerOpen(null);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-accent transition-colors ${
                              actionRecipients[action.id]?.id === s.id ? 'bg-primary/5' : ''
                            }`}
                            data-testid={`recipient-option-${s.id}`}>
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[8px] font-bold shrink-0">
                              {s.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{s.name}</span>
                              {s.is_primary_mapped && (
                                <span className="ml-1 text-[8px] text-violet-600">mapped</span>
                              )}
                            </div>
                            <span className="text-[8px] text-muted-foreground">{s.role?.replace(/_/g,' ')}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-[10px] px-3 gap-1"
                    onClick={() => handleExecuteAction(action.id, 'done_by_provider')}
                    data-testid={`action-done-${i}`}>
                    <Check className="w-3 h-3" /> Done
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-[10px] text-muted-foreground"
                    onClick={() => handleExecuteAction(action.id, 'dismiss')}
                    data-testid={`action-dismiss-${i}`}>
                    Dismiss
                  </Button>
                </div>
              ) : (
                <div className="ml-8 mt-1.5">
                  <Badge variant="outline" className={`text-[9px] ${action.status === 'sent_to_nurse' ? 'bg-blue-50 text-blue-600' : action.status === 'done_by_provider' ? 'bg-emerald-50 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                    {action.status === 'sent_to_nurse' ? 'Sent to Nurse' : action.status === 'done_by_provider' ? 'Done' : 'Dismissed'}
                  </Badge>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Action Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-action-modal">
          <div className="bg-card rounded-xl border shadow-2xl w-full max-w-md p-5 space-y-4 animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Add Clinical Action</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 rounded hover:bg-accent"><X className="w-4 h-4" /></button>
            </div>

            {/* Action Type */}
            <div>
              <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Type</label>
              <div className="flex gap-1.5 mt-1 flex-wrap">
                {[
                  { value: 'injection', label: 'Shot/Injection' },
                  { value: 'referral', label: 'Referral' },
                  { value: 'task', label: 'Task' },
                  { value: 'follow_up', label: 'Follow-Up' },
                  { value: 'form', label: 'Form' },
                ].map(t => (
                  <button key={t.value} onClick={() => setAddForm(f => ({ ...f, action_type: t.value }))}
                    className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${addForm.action_type === t.value ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                    data-testid={`action-type-${t.value}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Description</label>
              <input type="text" value={addForm.action_name}
                onChange={e => setAddForm(f => ({ ...f, action_name: e.target.value }))}
                placeholder={addForm.action_type === 'injection' ? 'e.g. Rocephin 1g IM' : addForm.action_type === 'referral' ? 'e.g. Cardiac Referral' : 'e.g. Schedule echocardiogram'}
                className="w-full mt-1 text-xs border rounded-md px-2.5 py-1.5 bg-background"
                data-testid="action-name-input" />
            </div>

            {/* Referral-specific fields */}
            {addForm.action_type === 'referral' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Specialty</label>
                  <input type="text" value={addForm.specialty}
                    onChange={e => setAddForm(f => ({ ...f, specialty: e.target.value }))}
                    placeholder="e.g. Cardiology" className="w-full mt-1 text-xs border rounded-md px-2.5 py-1.5 bg-background" />
                </div>
                <div>
                  <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Specialist Name</label>
                  <input type="text" value={addForm.specialist_name}
                    onChange={e => setAddForm(f => ({ ...f, specialist_name: e.target.value }))}
                    placeholder="e.g. Dr. Smith" className="w-full mt-1 text-xs border rounded-md px-2.5 py-1.5 bg-background" />
                </div>
              </div>
            )}

            {/* Injection J-code */}
            {addForm.action_type === 'injection' && (
              <div>
                <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">J-Code (Procedure Code)</label>
                <input type="text" value={addForm.j_code}
                  onChange={e => setAddForm(f => ({ ...f, j_code: e.target.value }))}
                  placeholder="e.g. J0696 (Rocephin), J3301 (Kenalog)"
                  className="w-full mt-1 text-xs border rounded-md px-2.5 py-1.5 bg-background font-mono" />
              </div>
            )}

            {/* Diagnosis Linkage */}
            {uniqueDxCodes.length > 0 && (
              <div>
                <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Link to Diagnosis</label>
                <select value={addForm.linked_diagnosis} onChange={e => setAddForm(f => ({ ...f, linked_diagnosis: e.target.value }))}
                  className="w-full mt-1 text-xs border rounded-md px-2.5 py-1.5 bg-background" data-testid="action-dx-select">
                  <option value="">No linkage</option>
                  {uniqueDxCodes.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}

            {/* Assign To */}
            <div>
              <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Assign To</label>
              <div className="flex gap-1.5 mt-1">
                {[{ value: 'nurse', label: 'Nurse' }, { value: 'provider', label: 'Me' }, { value: 'front_desk', label: 'Front Desk' }].map(r => (
                  <button key={r.value} onClick={() => setAddForm(f => ({ ...f, assignee: r.value }))}
                    className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${addForm.assignee === r.value ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Notes (optional)</label>
              <textarea value={addForm.summary} onChange={e => setAddForm(f => ({ ...f, summary: e.target.value }))}
                placeholder="Additional instructions..." rows={2}
                className="w-full mt-1 text-xs border rounded-md px-2.5 py-1.5 bg-background resize-none" />
            </div>

            <div className="flex gap-2 pt-1">
              <Button className="flex-1 text-xs gap-1.5" onClick={handleAddAction} disabled={adding} data-testid="add-action-confirm">
                {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add Action
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


